import React, { useState, useEffect, useCallback, useRef } from "react";

const HANDLE_SIZE = 12;
const MIN_SIZE = 40;

const handlePositions = {
  nw: { top: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, cursor: "nwse-resize" },
  ne: { top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, cursor: "nesw-resize" },
  sw: { bottom: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, cursor: "nesw-resize" },
  se: { bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, cursor: "nwse-resize" },
};

function CollageElement({
  element,
  isSelected,
  mode,
  canvasWidth,
  canvasHeight,
  onClick,
  onUpdate,
}) {
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(null); // "nw"|"ne"|"sw"|"se" or null
  const dragStartRef = useRef(null);

  // Move drag
  const handleMouseDown = useCallback(
    (e) => {
      if (resizing) return;
      e.stopPropagation();
      onClick();
      if (mode === "snip") return;

      dragStartRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: element.x,
        origY: element.y,
      };
      setDragging(true);
    },
    [element.x, element.y, mode, onClick, resizing]
  );

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e) => {
      const { startX, startY, origX, origY } = dragStartRef.current;
      let newX = origX + (e.clientX - startX);
      let newY = origY + (e.clientY - startY);
      // Clamp to canvas bounds
      newX = Math.max(0, Math.min(canvasWidth - element.width, newX));
      newY = Math.max(0, Math.min(canvasHeight - element.height, newY));
      onUpdate({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, canvasWidth, canvasHeight, element.width, element.height, onUpdate]);

  // Resize drag
  const handleResizeMouseDown = useCallback(
    (corner, e) => {
      e.stopPropagation();
      e.preventDefault();
      dragStartRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: element.x,
        origY: element.y,
        origW: element.width,
        origH: element.height,
      };
      setResizing(corner);
    },
    [element.x, element.y, element.width, element.height]
  );

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const { startX, startY, origX, origY, origW, origH } = dragStartRef.current;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      let newX = origX;
      let newY = origY;
      let newW = origW;
      let newH = origH;

      if (resizing === "se") {
        newW = Math.max(MIN_SIZE, origW + dx);
        newH = Math.max(MIN_SIZE, origH + dy);
      } else if (resizing === "sw") {
        newW = Math.max(MIN_SIZE, origW - dx);
        newH = Math.max(MIN_SIZE, origH + dy);
        newX = origX + origW - newW;
      } else if (resizing === "ne") {
        newW = Math.max(MIN_SIZE, origW + dx);
        newH = Math.max(MIN_SIZE, origH - dy);
        newY = origY + origH - newH;
      } else if (resizing === "nw") {
        newW = Math.max(MIN_SIZE, origW - dx);
        newH = Math.max(MIN_SIZE, origH - dy);
        newX = origX + origW - newW;
        newY = origY + origH - newH;
      }

      // Clamp to canvas
      newX = Math.max(0, newX);
      newY = Math.max(0, newY);
      if (newX + newW > canvasWidth) newW = canvasWidth - newX;
      if (newY + newH > canvasHeight) newH = canvasHeight - newY;

      onUpdate({ x: newX, y: newY, width: newW, height: newH });
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing, canvasWidth, canvasHeight, onUpdate]);

  return (
    <div
      style={{
        position: "absolute",
        left: element.x,
        top: element.y,
        width: element.width,
        height: element.height,
        outline: isSelected ? "2px solid #00a4e4" : "none",
        cursor: mode === "snip" ? "crosshair" : dragging ? "grabbing" : "grab",
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
    >
      <img
        src={element.url}
        alt=""
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none",
        }}
      />
      {isSelected && mode !== "snip" &&
        Object.entries(handlePositions).map(([corner, pos]) => (
          <div
            key={corner}
            style={{
              position: "absolute",
              width: HANDLE_SIZE,
              height: HANDLE_SIZE,
              background: "#00a4e4",
              border: "1px solid #fff",
              cursor: pos.cursor,
              ...pos,
            }}
            onMouseDown={(e) => handleResizeMouseDown(corner, e)}
          />
        ))}
    </div>
  );
}

export default CollageElement;
